import axios from "axios";
import { axiosInstance } from "contexts/axiosApiInstance";
import { createLogger } from "debug/DebugingLogger";
import { TableInfo } from "target_wasm";
const log = createLogger("DirectoryServices");

export const fetchTables = async (model_id: string): Promise<TableInfo[]> => {
  try {
    const response = await axiosInstance.get(`/action/${model_id}/tables`);
    // Pass the server response through unchanged so per-row `affordances`
    // (server-hydrated `TableAffordances`) survives to the import menu.
    // Stripping fields here is what previously hid the parent-lock signal
    // from `TableItem`.
    return response.data;
  } catch (error) {
    log.error("Error fetching tables data:", error);
    return [];
  }
};

/// Per-table import eligibility for a set of discovered tables (search hits,
/// dataset members). `can_import` / `conditional_import` drive the row's
/// muted/locked state and the direct-vs-request drop routing. The authoritative
/// table version is resolved server-side at import fulfillment, so a loose hit
/// needs no full `TableInfo` to become draggable.
export interface TableImportEligibility {
  table_id: string;
  can_import: boolean;
  conditional_import: boolean;
  /// D27: the space is sealed. Without a named destination this is why both
  /// booleans are false; with one, `can_import` may be true (D40 — the move
  /// stays inside the wall) while this stays true for the tooltip.
  sealed: boolean;
}

export const fetchTableImportEligibility = async (
  tableIds: string[],
  targetModelId?: string,
): Promise<TableImportEligibility[]> => {
  if (tableIds.length === 0) return [];
  try {
    // Naming the destination lets the server judge a sealed source against
    // the D40 boundary: importable when the move stays inside its wall.
    const response = await axiosInstance.post(`/api/tables/affordances`, {
      table_ids: tableIds,
      ...(targetModelId ? { target_model_id: targetModelId } : {}),
    });
    return response.data;
  } catch (error) {
    log.error("Error fetching table import eligibility:", error);
    return [];
  }
};

/// Server batch-affordances endpoint caps the request; larger sets (a big
/// dataset's members) are chunked and merged client-side.
const ELIGIBILITY_BATCH = 100;

export const fetchTableImportEligibilityChunked = async (
  tableIds: string[],
  targetModelId?: string,
): Promise<TableImportEligibility[]> => {
  const chunks: string[][] = [];
  for (let i = 0; i < tableIds.length; i += ELIGIBILITY_BATCH) {
    chunks.push(tableIds.slice(i, i + ELIGIBILITY_BATCH));
  }
  const results = await Promise.all(
    chunks.map((chunk) => fetchTableImportEligibility(chunk, targetModelId)),
  );
  return results.flat();
};

/// Single-table `TableInfo` for a table the user may only *discover* (a
/// cross-model graph endmember). Same hydration as the picker's list calls,
/// scoped to one id so it works without access to the parent model.
export const fetchTableInfo = async (
  table_id: string,
): Promise<TableInfo | null> => {
  try {
    const response = await axiosInstance.get(`/action/table_info/${table_id}`);
    return response.data;
  } catch (error) {
    log.error("Error fetching table info:", error);
    return null;
  }
};

/// Result of a cross-model import request. `locked_to_parent` is the
/// parent-lock guard's 403 — the table is connector-driven and can't be
/// imported elsewhere. UI surfaces this with a dedicated toast.
///
/// `error_code` and `message` are passed through from structured server
/// responses when available — `notifyImportFailure` reads them to pick a
/// curated user-facing message instead of the generic "could not request
/// access" fallback.
export type RequestTableImportResult =
  | { kind: "ok"; status: number }
  | { kind: "locked_to_parent"; message: string }
  | {
      kind: "error";
      status: number;
      error_code?: string;
      message?: string;
    };

export type ValuesAccessStatus =
  "sent" | "already_requested" | "already_readable" | "error";

/// D37: ask the parent model's owner \u2014 or its space's managers \u2014 for
/// data access to a values-blanked table. The server resolves who approves;
/// the client only names the table and where it is viewing it.
/// The caller's ask-state for a table's values. "none" doubles as the safe
/// answer on any failure: the chip then offers the request, and the server
/// dedupes a re-ask anyway.
export const fetchValuesAccessStatus = async (
  tableId: string,
): Promise<"pending" | "none"> => {
  try {
    const response = await axiosInstance.get(
      `/action/tables/${tableId}/values_access_status`,
    );
    return (response.data as { status?: string })?.status === "pending"
      ? "pending"
      : "none";
  } catch {
    return "none";
  }
};

export const requestTableValuesAccess = async (
  tableId: string,
  tableName: string,
  viewingModelId: string,
): Promise<ValuesAccessStatus> => {
  try {
    const response = await axiosInstance.post(
      `/action/request_table_values_access`,
      {
        table_id: tableId,
        table_name: tableName,
        viewing_model_id: viewingModelId,
      },
    );
    const status = (response.data as { status?: string })?.status;
    if (
      status === "sent" ||
      status === "already_requested" ||
      status === "already_readable"
    ) {
      return status;
    }
    return "error";
  } catch (error) {
    log.error("Error requesting table data access:", error);
    return "error";
  }
};

export const requestTableImport = async (
  table: TableInfo,
  targetModelId: string,
  parentModelId: string,
): Promise<RequestTableImportResult> => {
  try {
    const response = await axiosInstance.post(`/action/request_table_import`, {
      table_name: table.name,
      table_id: table.table_id,
      parent_model_id: parentModelId,
      target_model_id: targetModelId,
      table_version: Number(table.latest_version),
    });
    return { kind: "ok", status: response.status };
  } catch (error) {
    // Parent-lock guard returns 403 with a structured body so the toast
    // text matches the server-side message verbatim.
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 403 &&
      (error.response.data as { error_code?: string })?.error_code ===
        "table_locked_to_parent"
    ) {
      const data = error.response.data as { message?: string };
      return {
        kind: "locked_to_parent",
        message:
          data.message ??
          "This table is locked to its parent model and can't be imported elsewhere.",
      };
    }
    log.error("Error requesting table import:", error);
    if (axios.isAxiosError(error)) {
      const status = error.response?.status ?? 0;
      const body = error.response?.data as
        { error_code?: string; message?: string } | undefined;
      return {
        kind: "error",
        status,
        error_code: body?.error_code,
        message: body?.message,
      };
    }
    return { kind: "error", status: 500 };
  }
};
